// customer-sweepstakes-panel.js
if (window.CustomerSweepstakesPanel) {
  console.debug('CustomerSweepstakesPanel already loaded');
} else {

  class CustomerSweepstakesPanel {
    constructor(supabaseUrl, supabaseKey) {
      this.supabaseUrl = supabaseUrl;
      this.supabaseKey = supabaseKey;
      this.customerId = null;
      this.cache = new Map();
      this.cacheExpiry = 2 * 60 * 1000; // 2 minutes for customer data
      this.selectedRaffleId = null;
      this.raffles = [];
      this.dailyStreakCache = null;
      this.dailyStreakFlight = null;
      this.dailyStreakGeneration = 0;
      this.readScope = null;
      this.readVersion = 0;
      this.readProof = null;
      this.readRenewal = null;
      this.readFlights = new Map();
      this.readAuthFailureUntil = 0;
    }

    init() {
      const container = document.querySelector('.customer-sweepstakes-section[data-customer-id]');
      if (!container) return;

      this.customerId = container.dataset.customerId;
      if (!this.customerId) {
        console.warn('Customer ID not found');
        return;
      }

      const hmac = window.__KAIDO_HMAC || {};
      if (typeof hmac.customerId !== 'number' || Number.isSafeInteger(hmac.customerId)) {
        this.readProof = this.parseReadProof({ customer_id: String(hmac.customerId || ''),
          token: hmac.token, timestamp: hmac.timestamp }, String(this.customerId));
      }

      // Invalidate pending private reads even across an account/DOM change and return.
      if (typeof MutationObserver === 'function') {
        new MutationObserver(changes => {
          const identityChanged = changes.some(change =>
            (change.type === 'attributes' && change.target === container) ||
            Array.from(change.removedNodes || []).some(node => node === container || node.contains?.(container)));
          if (identityChanged) this.invalidateReadScope();
        }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-customer-id'] });
      }

      this.loadRaffleAndData();
      document.addEventListener('kaido:entries-updated', event => {
        if (String(event.detail?.customerId || '') !== String(this.customerId)) return;
        this.cache.clear();
        this.loadCustomerData();
      });
      this.initializeModal();
      this.generateRandomMaze();
      // Shared popup state has no REAL-state event; focus revalidates this card only.
      window.addEventListener('focus', () => this.loadDailyStreak());
      const drawer = document.querySelector('.kv2f');
      if (drawer && typeof MutationObserver === 'function') {
        let wasOpen = !!drawer.querySelector('.pp.is-open'), closeTimer;
        new MutationObserver(changes => {
          if (!changes.some(change => change.target.classList.contains('pp'))) return;
          if (drawer.querySelector('.pp.is-open')) {
            wasOpen = true;
            clearTimeout(closeTimer);
          } else if (wasOpen) {
            clearTimeout(closeTimer);
            // Scratch -> wheel hands off on the next frame; only the final close refreshes.
            closeTimer = setTimeout(() => {
              if (drawer.querySelector('.pp.is-open')) return;
              wasOpen = false;
              this.dailyStreakCache = null;
              this.dailyStreakGeneration++;
              this.loadDailyStreak();
            }, 250);
          }
        }).observe(drawer, { attributes: true, attributeFilter: ['class'], subtree: true });
      }
    }

    invalidateReadScope() {
      this.readVersion++;
      this.readScope = null;
      this.readProof = null;
      this.readRenewal = null;
      this.cache.clear();
      this.fullHistory = [];
      for (const id of ['total-entries-value', 'free-entries-value']) {
        const el = document.getElementById(id);
        if (el) el.textContent = 'Unavailable';
      }
      for (const id of ['entry-history-list', 'modal-history-list', 'detail-modal-body', 'detail-modal-title']) {
        const el = document.getElementById(id);
        if (el) el.textContent = '';
      }
    }

    captureReadScope() {
      const customerId = String(this.customerId || '');
      const container = document.querySelector('.customer-sweepstakes-section[data-customer-id]');
      if (!/^[1-9][0-9]*$/.test(customerId) || !container || container.dataset.customerId !== customerId) {
        this.invalidateReadScope();
        throw new Error('Account identity unavailable');
      }
      const raffleId = this.selectedRaffleId;
      if (this.readScope && (this.readScope.customerId !== customerId || this.readScope.raffleId !== raffleId)) {
        const changedAccount = this.readScope.customerId !== customerId;
        this.readVersion++;
        this.cache.clear();
        this.fullHistory = [];
        if (changedAccount) { this.readProof = null; this.readRenewal = null; }
      }
      this.readScope = { customerId, raffleId, version: this.readVersion };
      return this.readScope;
    }

    isReadScopeCurrent(scope) {
      const container = document.querySelector('.customer-sweepstakes-section[data-customer-id]');
      return scope.version === this.readVersion && String(this.customerId || '') === scope.customerId &&
        container?.dataset.customerId === scope.customerId && this.selectedRaffleId === scope.raffleId;
    }

    assertReadScope(scope) {
      if (!this.isReadScopeCurrent(scope)) throw new Error('Account read superseded');
    }

    parseReadProof(value, customerId) {
      if (!value || value.customer_id !== customerId || !/^[1-9][0-9]*$/.test(customerId) ||
          typeof value.token !== 'string' || !/^[0-9a-f]{64}$/.test(value.token) ||
          !['string', 'number'].includes(typeof value.timestamp) || !/^[0-9]+$/.test(String(value.timestamp))) return null;
      const timestamp = Number(value.timestamp);
      if (!Number.isSafeInteger(timestamp) || timestamp <= 0) return null;
      return { customerId, token: value.token, timestamp };
    }

    async readFetch(url, options) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, { ...options, cache: 'no-store', redirect: 'error', signal: controller.signal });
        // Include body consumption in the deadline; never log private URLs or proofs.
        const body = await response.text();
        return { status: response.status, ok: response.ok, body };
      } finally { clearTimeout(timer); }
    }

    async renewReadProof(scope, failed) {
      this.assertReadScope(scope);
      const newer = this.readProof;
      const newerAge = newer ? Date.now() / 1000 - newer.timestamp : Infinity;
      if (newer && newer.customerId === scope.customerId && newer.timestamp > failed.timestamp &&
          newer.token !== failed.token && newerAge >= -5 && newerAge <= 30) return newer;
      // Keep failed flights briefly as well, so concurrent failures cannot start a renewal storm.
      let flight = this.readRenewal;
      if (!flight || flight.customerId !== scope.customerId || flight.token !== failed.token || Date.now() >= flight.until) {
        flight = { customerId: scope.customerId, token: failed.token, until: Date.now() + 30000 };
        flight.promise = (async () => {
          const started = Math.floor(Date.now() / 1000);
          const root = window.Shopify?.routes?.root || '/';
          if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//')) throw new Error('Invalid account route');
          const url = new URL(root, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid account origin');
          url.searchParams.set('section_id', 'kaido-account-read-identity');
          url.searchParams.set('_kaido_read_nonce', window.crypto.randomUUID());
          const response = await this.readFetch(url.href, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'text/html' } });
          this.assertReadScope(scope);
          if (!response.ok) throw new Error('Account renewal unavailable');
          const doc = new DOMParser().parseFromString(response.body, 'text/html');
          const nodes = doc.querySelectorAll('script#kaido-account-read-identity[type="application/json"]');
          if (nodes.length !== 1) throw new Error('Account renewal unavailable');
          const proof = this.parseReadProof(JSON.parse(nodes[0].textContent), scope.customerId);
          const now = Date.now() / 1000;
          // Cache-busting is not freshness proof. Reject old cached Liquid timestamps and future clocks.
          if (!proof || proof.timestamp <= failed.timestamp || proof.token === failed.token ||
              proof.timestamp < started - 5 || now - proof.timestamp > 30 || proof.timestamp - now > 5) {
            throw new Error('Account renewal rejected');
          }
          this.assertReadScope(scope);
          this.readProof = proof;
          return proof;
        })();
        this.readRenewal = flight;
      }
      const proof = await flight.promise;
      this.assertReadScope(scope);
      return proof;
    }

    async privateRead(kind, scope, limit = null) {
      this.assertReadScope(scope);
      if (Date.now() < this.readAuthFailureUntil) throw new Error('Account authentication unavailable');
      const key = JSON.stringify([scope.version, scope.customerId, scope.raffleId, kind, limit]);
      if (this.readFlights.has(key)) return this.readFlights.get(key);
      const promise = (async () => {
        let proof = this.readProof;
        if (!proof || proof.customerId !== scope.customerId) throw new Error('Account proof unavailable');
        for (let attempt = 0; attempt < 2; attempt++) {
          this.assertReadScope(scope);
          const headers = { apikey: this.supabaseKey, Authorization: `Bearer ${this.supabaseKey}` };
          let url = `${this.supabaseUrl}/functions/v1/get_customer_entries/raffle_entries`;
          let options = { method: 'POST', credentials: 'omit', headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ p_customer_id: scope.customerId, p_raffle_id: scope.raffleId,
              hmac_token: proof.token, hmac_timestamp: proof.timestamp }) };
          if (kind === 'history') {
            const params = new URLSearchParams({ customer_id: `eq.${scope.customerId}`, order: 'created_at.desc',
              hmac_token: proof.token, hmac_timestamp: String(proof.timestamp) });
            if (scope.raffleId) params.set('raffle_id', `eq.${scope.raffleId}`);
            if (limit !== null) params.set('limit', String(limit));
            url = `${this.supabaseUrl}/functions/v1/get_customer_entries/rest/v1/sweepstakes_entries?${params}`;
            options = { method: 'GET', credentials: 'omit', headers };
          }
          const response = await this.readFetch(url, options);
          this.assertReadScope(scope);
          let data;
          try { data = JSON.parse(response.body); } catch (_) { throw new Error('Invalid account response'); }
          if (response.status === 401 && attempt === 0 && data?.error === 'Token expired') {
            try { proof = await this.renewReadProof(scope, proof); }
            catch (_) {
              if (this.isReadScopeCurrent(scope)) {
                this.readAuthFailureUntil = Date.now() + 30000;
                this.invalidateReadScope();
              }
              throw new Error('Account renewal unavailable');
            }
            continue;
          }
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              this.readAuthFailureUntil = Date.now() + 30000;
              this.invalidateReadScope();
            }
            throw new Error('Account read unavailable');
          }
          if (kind === 'history' && !Array.isArray(data)) throw new Error('Invalid account history');
          return data;
        }
      })();
      this.readFlights.set(key, promise);
      try { return await promise; }
      finally { if (this.readFlights.get(key) === promise) this.readFlights.delete(key); }
    }

    async loadRaffleAndData() {
      try {
        const raffles = await this.fetchRafflePicker();
        this.raffles = raffles || [];
        this.selectedRaffleId = raffles && raffles.length ? raffles[0].id : null;
        this.populateRaffleDropdown(raffles);
        await this.loadCustomerData();
      } catch (error) {
        console.error('Error loading raffle picker:', error);
        await this.loadCustomerData();
      }
    }

    populateRaffleDropdown(raffles) {
      const select = document.getElementById('raffle-select');
      if (!select) return;
      select.innerHTML = '';
      if (!raffles || raffles.length === 0) {
        select.innerHTML = '<option>No giveaways available</option>';
        select.disabled = true;
        return;
      }
      select.disabled = false;
      select.innerHTML = raffles
        .map((raffle) => {
          const label =
            raffle.status === 'active'
              ? `${raffle.name || 'Current Giveaway'} (Active)`
              : `${raffle.name || 'Giveaway'} (Completed)`;
          return `<option value="${raffle.id}">${label}</option>`;
        })
        .join('');

      select.value = this.selectedRaffleId || raffles[0].id;
      select.addEventListener('change', async (e) => {
        this.readVersion++;
        this.fullHistory = [];
        this.selectedRaffleId = e.target.value || null;
        // clear cache for this customer/raffle combination
        this.cache.delete(`customer_entries_${this.customerId}_${this.selectedRaffleId || 'none'}`);
        await this.loadCustomerData();
      });
    }

    async loadCustomerData() {
      await Promise.all([
        this.loadStats(),
        this.loadEntryHistory(),
        this.loadActivePromotions(),
        this.loadDailyStreak()
      ]).catch(error => {
        console.error('Error loading customer data:', error);
      });
    }

    async fetchRafflePicker() {
      try {
        const response = await fetch(
          `${this.supabaseUrl}/functions/v1/get_customer_entries/raffle_picker`,
          {
            headers: {
              apikey: this.supabaseKey,
              Authorization: `Bearer ${this.supabaseKey}`,
            },
          },
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        let data = await response.json();
        // Normalize to array in case API returns a single object
        if (!Array.isArray(data) && data) data = [data];
        return data;
      } catch (error) {
        console.error('Error fetching raffle picker:', error);
        return null;
      }
    }

    async loadDailyStreak() {
      const valueEl = document.getElementById('daily-streak-value');
      const wheelEl = document.getElementById('daily-wheel-ready');
      if (!valueEl || !wheelEl) return;
      const paint = (value, wheel = '') => { valueEl.textContent = value; wheelEl.textContent = wheel; };
      const customerId = String(this.customerId || ''), raffleId = this.selectedRaffleId;
      const key = `${customerId}:${raffleId}`;
      const raffle = this.raffles.find(item => item.id === raffleId);
      if (!customerId || !raffleId || !raffle || raffle.status !== 'active') {
        this.dailyStreakGeneration++;
        paint(raffle && raffle.status !== 'active' ? 'Not available' : 'Unavailable');
        return;
      }
      if (this.dailyStreakCache?.key === key && Date.now() < this.dailyStreakCache.freshUntil) {
        paint(this.dailyStreakCache.value, this.dailyStreakCache.wheel);
        return;
      }
      if (this.dailyStreakFlight?.key === key && this.dailyStreakFlight.generation === this.dailyStreakGeneration) return this.dailyStreakFlight.promise;
      const generation = ++this.dailyStreakGeneration;
      const current = () => generation === this.dailyStreakGeneration && this.selectedRaffleId === raffleId && String(this.customerId || '') === customerId;
      if (this.dailyStreakCache?.key !== key || Date.now() >= this.dailyStreakCache.until) paint('Loading...');
      const promise = (async () => {
        try {
          // Defer/script order may expose the shared client after panel initialization.
          for (let i = 0; !window.KaidoV2p?.call && i < 20; i++) await new Promise(resolve => setTimeout(resolve, 100));
          if (!current()) return;
          if (!window.KaidoV2p?.call) throw new Error('Daily state client unavailable');
          const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (!zone) throw new Error('Daily timezone unavailable');
          // Non-awarding state call; existing identity bootstrap can pin the first eligible timezone.
          const state = await window.KaidoV2p.call('real_state', customerId, { time_zone: zone });
          if (!current()) return;
          if (state?.ok === true && state.real === false) { paint('Not available'); return; }
          if (!state || state.ok !== true || state.real !== true) throw new Error('Daily state unavailable');
          if (typeof state.raffle_id !== 'string' || !state.raffle_id) throw new Error('Invalid daily raffle');
          if (state.raffle_id !== raffleId) { paint('Not available'); return; }
          if (!Number.isSafeInteger(state.streak) || state.streak < 0 ||
              !Number.isSafeInteger(state.streak_target) || state.streak_target < 1 ||
              state.streak > state.streak_target || !Array.isArray(state.spins_available)) throw new Error('Invalid daily state');
          const serverAt = Date.parse(state.server_time), resetAt = Date.parse(state.next_reset_at);
          if (!Number.isFinite(serverAt) || !Number.isFinite(resetAt) || resetAt <= serverAt) throw new Error('Invalid daily clock');
          const ids = new Set();
          let ttl = resetAt - serverAt;
          for (const spin of state.spins_available) {
            const expires = Date.parse(spin?.expires_at);
            if (typeof spin?.entitlement_id !== 'string' || !spin.entitlement_id || !Number.isFinite(expires)) throw new Error('Invalid daily spin');
            if (expires > serverAt) { ids.add(spin.entitlement_id); ttl = Math.min(ttl, expires - serverAt); }
          }
          const value = `${state.streak} / ${state.streak_target}`;
          const wheel = ids.size ? `${ids.size} ${ids.size === 1 ? 'spin' : 'spins'} ready` : 'No spins ready';
          this.dailyStreakCache = { key, until: Date.now() + ttl, freshUntil: Date.now() + Math.min(30000, ttl), value, wheel };
          paint(value, wheel);
        } catch (error) {
          if (current()) { this.dailyStreakCache = null; paint('Unavailable'); }
        }
      })();
      this.dailyStreakFlight = { key, generation, promise };
      try { await promise; } finally {
        if (this.dailyStreakFlight?.promise === promise) this.dailyStreakFlight = null;
      }
    }

    async loadStats() {
      let scope;
      try {
        scope = this.captureReadScope();
        const data = await this.fetchCustomerEntries(scope);
        this.assertReadScope(scope);
        const totalEl = document.getElementById('total-entries-value');
        const freeEl = document.getElementById('free-entries-value');

        if (totalEl) totalEl.textContent = this.formatNumber(data.total_entries);
        if (freeEl) {
          freeEl.textContent = this.formatNumber(data.total_entries);
        }

      } catch (error) {
        if (scope && !this.isReadScopeCurrent(scope)) return;
        console.error('Error loading stats:', error);
        ['total-entries-value', 'free-entries-value'].forEach(id => {
          const el = document.getElementById(id);
          if (el) el.textContent = 'Unavailable';
        });
      }
    }

    async fetchCustomerEntries(scope = this.captureReadScope()) {
      this.assertReadScope(scope);
      const cacheKey = `customer_entries_${scope.customerId}_${scope.raffleId || 'none'}`;
      const cachedData = this.cache.get(cacheKey);

      if (cachedData && Date.now() - cachedData.timestamp < this.cacheExpiry) {
        return cachedData.data;
      }

      try {
        const data = await this.privateRead('summary', scope);
        this.assertReadScope(scope);
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid entries summary');
        const result = { ...data };
        for (const field of ['total_entries', 'recent_entries']) {
          const value = data[field];
          if (!(typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) ||
              !Number.isSafeInteger(Number(value)) || Number(value) < 0) throw new Error('Invalid entries summary');
          result[field] = Number(value);
        }

        this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      } catch (error) {
        console.error("Error fetching customer entries:", error);
        throw error;
      }
    }

    async loadEntryHistory(limit = 5) {
      let scope;
      try {
        scope = this.captureReadScope();
        const entries = await this.privateRead('history', scope, limit);
        this.assertReadScope(scope);
        this.renderEntryHistory(entries, 'entry-history-list');
        // Store full history for modal
        this.fullHistory = entries;
        if(limit === 5 && entries.length === 5) {
          // pre-fetch full history for modal if there are more
          this.fetchAllEntryHistory();
        }
      } catch (error) {
        if (scope && !this.isReadScopeCurrent(scope)) return;
        this.fullHistory = [];
        console.error('Error loading entry history:', error);
        const historyContainer = document.getElementById('entry-history-list');
        if (historyContainer) historyContainer.innerHTML = '<li>Error loading history.</li>';
      }
    }

    async fetchAllEntryHistory() {
        let scope;
        try {
            scope = this.captureReadScope();
            const entries = await this.privateRead('history', scope);
            this.assertReadScope(scope);
            this.fullHistory = entries;
        } catch (error) {
            if (scope && !this.isReadScopeCurrent(scope)) return;
            console.error('Error fetching all entry history:', error);
        }
    }

    renderEntryHistory(entries, containerId) {
      const container = document.getElementById(containerId);
      if (!container) return;

      // Ensure timeline class is present
      if (!container.classList.contains('timeline-list')) {
        container.classList.add('timeline-list');
      }

      if (!entries || entries.length === 0) {
        container.innerHTML = '<p class="history-empty">No entries yet. Shop now to earn your first entries!</p>';
        return;
      }

      container.innerHTML = entries.map((entry, index) => {
        const date = new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const entryType = this.getEntryType(entry);
        const entryDisplay = this.getEntryDisplay(entry, entryType);
        const title = entryDisplay.title;
        const subtitle = entryDisplay.subtitle;

        const spentHtml = (entryType === 'purchase' && entry.order_subtotal)
            ? `<span class="timeline-card-spent">$${parseFloat(entry.order_subtotal).toFixed(2)}</span>`
            : '';

        return `
          <li class="timeline-item type-${entryType}">
            <div class="timeline-dot"></div>
            <div class="timeline-card" data-entry-index="${index}" role="button" tabindex="0" aria-label="View details">
              <p class="timeline-card-date">${date}</p>
              <div class="timeline-card-header">
                <div class="timeline-card-info">
                  <p class="timeline-card-title">${this.escapeHtml(title)}</p>
                  <p class="timeline-card-subtitle">${this.escapeHtml(subtitle)}</p>
                </div>
                <div class="timeline-card-meta">
                  <span class="timeline-entries-pill pill-${entryType}">+${this.formatNumber(entry.entries_earned || 0)}</span>
                  ${spentHtml}
                </div>
              </div>
            </div>
          </li>
        `;
      }).join('');

      // Attach click handlers for detail modal
      container.querySelectorAll('.timeline-card[data-entry-index]').forEach(card => {
        const handler = () => {
          const idx = parseInt(card.dataset.entryIndex);
          if (!isNaN(idx) && entries[idx]) {
            this.openEntryDetail(entries[idx]);
          }
        };
        card.addEventListener('click', handler);
        card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); } });
      });
    }

    openEntryDetail(entry) {
      const modal = document.getElementById('entry-detail-modal');
      const body = document.getElementById('detail-modal-body');
      const title = document.getElementById('detail-modal-title');
      if (!modal || !body) return;

      const date = new Date(entry.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
      const entryType = this.getEntryType(entry);
      const lineItems = entry.line_items || [];

      if (entryType === 'purchase') {
        const orderLabel = entry.order_display_name || entry.order_number || 'Order';
        title.textContent = `Order ${orderLabel}`;

        const placeholderSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`;

        let productsHtml = '';
        if (lineItems.length > 0) {
          productsHtml = `<div class="detail-products">${lineItems.map((li) => {
            const imgTag = li.product_image
              ? `<img class="detail-product-img" src="${li.product_image}" alt="">`
              : `<div class="detail-product-placeholder">${placeholderSvg}</div>`;
            const qtyLabel = li.quantity > 1 ? ` × ${li.quantity}` : '';
            return `
              <div class="detail-product-card">
                ${imgTag}
                <p class="detail-product-name">${(li.title || 'Product').toUpperCase()}${qtyLabel}</p>
              </div>`;
          }).join('')}</div>`;
        }

        body.innerHTML = `
          ${productsHtml}
          <div class="detail-summary">
            <div class="detail-stat">
              <p class="detail-stat-label">Entries Earned</p>
              <p class="detail-stat-value text-green">+${this.formatNumber(entry.entries_earned || 0)}</p>
            </div>
            <div class="detail-stat">
              <p class="detail-stat-label">Total Spent</p>
              <p class="detail-stat-value text-red">$${parseFloat(entry.order_subtotal || 0).toFixed(2)}</p>
            </div>
            <div class="detail-stat">
              <p class="detail-stat-label">Date</p>
              <p class="detail-stat-value" style="font-size:0.85rem">${date}</p>
            </div>
            <div class="detail-stat">
              <p class="detail-stat-label">Rate</p>
              <p class="detail-stat-value" style="font-size:0.85rem">${entry.entries_per_dollar || window.kaidoDefaultEntriesPerDollar()}/$ spent</p>
            </div>
          </div>
        `;

      } else {
        // Bonus / free / social entry
        const entryDisplay = this.getEntryDisplay(entry, entryType);
        title.textContent = entryDisplay.title;
        const detailLabel = entryDisplay.detailLabel;
        const sourceLabel = this.getFreeRewardKind(entry) ? entryDisplay.title
          : entry.display_title || entry.product_name || entryDisplay.title || 'Promotion';
        body.innerHTML = `
          <div class="detail-bonus-info">
            <p style="font-size:1.5rem">+${this.formatNumber(entry.entries_earned || 0)} Entries</p>
            <p class="detail-bonus-label">${this.escapeHtml(detailLabel)}</p>
          </div>
          <div class="detail-summary" style="margin-top:16px">
            <div class="detail-stat">
              <p class="detail-stat-label">Date</p>
              <p class="detail-stat-value" style="font-size:0.9rem">${date}</p>
            </div>
            <div class="detail-stat">
              <p class="detail-stat-label">Source</p>
              <p class="detail-stat-value" style="font-size:0.9rem">${this.escapeHtml(sourceLabel)}</p>
            </div>
          </div>
        `;
      }

      // Open the detail modal
      this._savedDetailOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      modal.classList.add('active');
    }

    getFreeRewardKind(entry) {
      const source = String(entry.order_number || entry.source_order_id || '');
      const match = /^V2_FREE_(CLAIM|SPIN)_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.exec(source);
      return match ? match[1].toUpperCase() : null;
    }

    getEntryType(entry) {
      if (this.getFreeRewardKind(entry) || ['social_bonus', 'admin_bonus', 'free', 'review_bonus', 'mailing_list_bonus'].includes(entry.entry_type)) return 'bonus';
      return entry.order_number ? 'purchase' : 'bonus';
    }

    getEntryDisplay(entry, entryType) {
      const freeReward = this.getFreeRewardKind(entry);
      if (freeReward) {
        const claimedAt = new Date(entry.created_at);
        const date = entry.created_at && Number.isFinite(claimedAt.getTime())
          ? claimedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
        const title = freeReward === 'CLAIM' ? 'Daily Scratch-Off' : 'Streak Wheel Reward';
        const description = date ? `${freeReward === 'CLAIM' ? 'Claimed' : 'Spin completed'} on ${date}`
          : freeReward === 'CLAIM' ? 'Daily scratch-off reward' : 'Streak wheel reward';
        return { title, subtitle: description, detailLabel: description };
      }
      const displayTitle = entry.display_title || null;
      const displayMessage = entry.display_message || null;

      if (displayTitle || displayMessage) {
        return {
          title: displayTitle || (entryType === 'purchase' ? 'Product Purchase' : 'Bonus Entry'),
          subtitle: displayMessage || (entry.order_display_name ? `Order ${entry.order_display_name}` : 'From promotion'),
          detailLabel: displayMessage || 'Bonus Entry',
        };
      }

      if (entryType === 'purchase') {
        const purchaseLabel = entry.order_display_name
          ? `Order ${entry.order_display_name}`
          : (entry.order_number ? `Order ${entry.order_number}` : (entry.product_name || 'From promotion'));
        return {
          title: entry.product_name || 'Product Purchase',
          subtitle: purchaseLabel,
          detailLabel: purchaseLabel,
        };
      }

      if (entry.entry_type === 'social_bonus') {
        return {
          title: 'Social Media Bonus',
          subtitle: 'Follow bonus reward',
          detailLabel: 'Follow bonus reward',
        };
      }

      if (entry.entry_type === 'admin_bonus') {
        return {
          title: 'Bonus Entries Reward',
          subtitle: 'Thank you for your help!',
          detailLabel: 'Thank you for helping keep our community safe!',
        };
      }

      if (entry.entry_type === 'free') {
        return {
          title: 'Free Daily Entry',
          subtitle: 'From promotion',
          detailLabel: 'Daily free entry claim',
        };
      }

      if (entry.entry_type === 'review_bonus') {
        return {
          title: 'Review Bonus',
          subtitle: 'Thank you for your review!',
          detailLabel: 'Thank you for your review!',
        };
      }

      if (entry.entry_type === 'mailing_list_bonus') {
        return {
          title: 'Mailing List Bonus',
          subtitle: 'Thanks for joining our mailing list!',
          detailLabel: 'Thanks for joining our mailing list!',
        };
      }

      return {
        title: entry.entry_type || 'Bonus Entry',
        subtitle: entry.product_name || 'From promotion',
        detailLabel: entry.entry_type || 'Bonus Entry',
      };
    }

    async loadActivePromotions() {
      const container = document.getElementById('active-promotions-list');
      if (!container) return;

      try {
        // Preferred source: collection-promo estimator endpoint (includes collection handles and base rate).
        const response = await fetch(
          `${this.supabaseUrl}/functions/v1/cart_entries_estimate/promotions`,
          {
            headers: { apikey: this.supabaseKey, Authorization: `Bearer ${this.supabaseKey}` },
          }
        );
        if (response.ok) {
          const payload = await response.json();
          const promotions = Array.isArray(payload?.promotions) ? payload.promotions : [];
          const baseRate = Number(payload?.base_rate || 0);
          this.renderActivePromotions(promotions, { baseRate });
          return;
        }
      } catch (error) {
        console.error('Error loading collection promotions:', error);
      }

      try {
        // Fallback for legacy promotion records.
        const response = await fetch(
          `${this.supabaseUrl}/functions/v1/get_customer_entries/rest/v1/promotions?active=eq.true`,
          {
            headers: { apikey: this.supabaseKey, Authorization: `Bearer ${this.supabaseKey}` },
          }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const promotions = await response.json();
        this.renderActivePromotions(Array.isArray(promotions) ? promotions : [], { baseRate: 0 });
      } catch (error) {
        console.error('Error loading promotions:', error);
        container.innerHTML =
          '<div style="display:flex;align-items:center;justify-content:center;min-height:120px;text-align:center;width:100%;">There are no active promotions.</div>';
      }
    }

    escapeHtml(input) {
      return String(input ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

    formatMultiplier(multiplier) {
      const num = Number(multiplier || 1);
      if (!Number.isFinite(num) || num <= 0) return '1x';
      return num % 1 === 0 ? `${num.toFixed(0)}x` : `${num.toFixed(1)}x`;
    }

    titleFromHandle(handle) {
      const safe = String(handle || '').trim();
      if (!safe) return '';
      const parts = safe.split('-').filter(Boolean).map((p) => p.charAt(0).toUpperCase() + p.slice(1));
      // Rejoin with hyphens when a single-letter segment precedes a word (e.g. t-shirts → T-Shirts)
      let result = parts[0] || '';
      for (let i = 1; i < parts.length; i++) {
        result += (parts[i - 1].length === 1 ? '-' : ' ') + parts[i];
      }
      return result;
    }

    joinLabels(labels) {
      if (!Array.isArray(labels) || labels.length === 0) return '';
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
      return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
    }

    getPromotionModeHint(mode, minDistinct) {
      if (mode === 'REQUIRE_EACH_LISTED_COLLECTION') {
        return 'You must include at least one item from each eligible collection.';
      }
      if (mode === 'MIN_DISTINCT_LISTED_COLLECTIONS') {
        const need = Math.max(1, Number(minDistinct || 1));
        return `You must include items from at least ${need} eligible collection${need === 1 ? '' : 's'}.`;
      }
      return 'Any eligible collection item counts toward the item threshold.';
    }

    buildShopHref(promo) {
      const collections = Array.isArray(promo?.collections) ? promo.collections : [];
      const firstWithHandle = collections.find((c) => c?.collection_handle_snapshot);
      if (firstWithHandle?.collection_handle_snapshot) {
        return `/collections/${encodeURIComponent(String(firstWithHandle.collection_handle_snapshot))}`;
      }
      return '/collections/all';
    }

    renderActivePromotions(promotions, options = {}) {
      const container = document.getElementById('active-promotions-list');
      if (!container) return;
      if (!promotions || promotions.length === 0) {
        container.innerHTML = `
          <div class="promo-empty-state">
            <div class="promo-empty-header">
              <div class="promo-empty-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
              </div>
              <div>
                <div class="promo-empty-heading">No active deals</div>
                <div class="promo-empty-sub">Promotions are paused right now</div>
              </div>
            </div>
            <div class="promo-empty-body">You still earn entries on every purchase. Shop the collection and rack up entries for the next giveaway draw.</div>
            <a href="/collections/all" class="promo-empty-btn">Shop &amp; Earn Entries</a>
          </div>
        `;
        return;
      }

      const baseRate = Number(options.baseRate || 0);

      container.innerHTML = promotions.map((promo) => {
        const name = this.escapeHtml(promo?.name || 'Special Offer');
        const multiplierValue = Number(promo?.multiplier || 1);
        const multiplierText = this.formatMultiplier(multiplierValue);
        const minQty = Math.max(1, Number(promo?.min_eligible_qty || 1));
        const modeHint = this.getPromotionModeHint(
          promo?.requirement_mode,
          promo?.min_distinct_collections_required
        );
        const collections = Array.isArray(promo?.collections) ? promo.collections : [];
        const collectionMeta = collections
          .map((c) => {
            const handle = String(c?.collection_handle_snapshot || '').trim();
            const collectionId = String(c?.shopify_collection_id || '').trim();
            if (handle) {
              const label = this.titleFromHandle(handle);
              return {
                label: label || handle,
                href: `/collections/${encodeURIComponent(handle)}`,
                html: `<a class="promo-collection-link" href="/collections/${encodeURIComponent(handle)}">${this.escapeHtml(label || handle)}</a>`,
              };
            }
            if (collectionId) {
              return {
                label: `Collection ${collectionId}`,
                href: null,
                html: `<span class="promo-collection-label">Collection ${this.escapeHtml(collectionId)}</span>`,
              };
            }
            return null;
          })
          .filter(Boolean);

        const collectionLabels = collectionMeta.map((c) => c.label);
        const collectionLinks = collectionMeta.map((c) => c.html);

        const joinedCollectionLabels = this.joinLabels(collectionLabels);
        const thresholdText = minQty === 1
          ? (joinedCollectionLabels
            ? `Add at least 1 item from ${joinedCollectionLabels}.`
            : 'Add at least 1 eligible item.')
          : (joinedCollectionLabels
            ? `Add ${minQty} or more items from ${joinedCollectionLabels} in the same order.`
            : `Add ${minQty} or more eligible items in the same order.`);

        const boostedRate = baseRate > 0 && Number.isFinite(multiplierValue)
          ? Math.round(baseRate * multiplierValue)
          : null;
        const rateLine = boostedRate
          ? `Eligible items earn <strong>${this.formatNumber(boostedRate)} entries per $1</strong>. Non-eligible items stay at ${this.formatNumber(baseRate)} entries per $1.`
          : '';
        const shopHref = this.buildShopHref(promo);
        const primaryCollection = collectionLabels[0] || 'eligible collection';
        const shopLabel = collectionLabels.length > 1
          ? 'Shop Eligible Collections'
          : `Shop ${primaryCollection}`;

        // Collection browse link under the rate line
        const browseLink = collectionLinks.length
          ? `<p class="promo-item-meta">Browse: ${collectionLinks.join(', ')}</p>`
          : '';

        return `
          <div class="promo-item">
            <p class="promo-item-kicker">Active deal</p>
            <p class="promo-item-title"><span class="brand-gold">${this.escapeHtml(multiplierText)}</span> Bonus Entries on Select Items</p>
            <p class="promo-item-desc">${this.escapeHtml(thresholdText)} This unlocks ${this.escapeHtml(multiplierText)} entries on those eligible items only.</p>
            <p class="promo-item-meta">${this.escapeHtml(modeHint)}</p>
            ${rateLine ? `<p class="promo-item-meta">${rateLine}</p>` : ''}
            ${browseLink}
            <div class="promo-item-actions">
              <a class="promo-shop-btn" href="${this.escapeHtml(shopHref)}">${this.escapeHtml(shopLabel)}</a>
            </div>
          </div>
        `;
      }).join('');
    }

    formatNumber(num) {
      return num.toLocaleString('en-US');
    }

    initializeModal() {
        const viewAllBtn = document.getElementById('view-all-btn');
        const closeModalBtn = document.getElementById('close-modal-btn');
        const modal = document.getElementById('history-modal');
        const modalContainer = modal ? modal.querySelector('.modal-container') : null;
        const dragHandle = modal ? modal.querySelector('.modal-drag-handle') : null;

        if (!viewAllBtn || !closeModalBtn || !modal || !modalContainer) return;

        let savedOverflow = '';

        const openModal = () => {
            this.renderEntryHistory(this.fullHistory || [], 'modal-history-list');
            savedOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            modal.classList.add('active');
        };

        const closeModal = () => {
            modal.classList.remove('active');
            document.body.style.overflow = savedOverflow;
        };

        viewAllBtn.addEventListener('click', openModal);
        closeModalBtn.addEventListener('click', closeModal);
        modal.addEventListener('click', (event) => {
            if (event.target === modal) closeModal();
        });

        // Detail modal setup
        const detailModal = document.getElementById('entry-detail-modal');
        const closeDetailBtn = document.getElementById('close-detail-modal-btn');
        if (detailModal && closeDetailBtn) {
            const closeDetail = () => {
                detailModal.classList.remove('active');
                document.body.style.overflow = this._savedDetailOverflow || '';
            };
            closeDetailBtn.addEventListener('click', closeDetail);
            detailModal.addEventListener('click', (e) => { if (e.target === detailModal) closeDetail(); });
        }
    }

    generateRandomMaze() {
        const pathElement = document.getElementById('maze-path');
        if (!pathElement) return;
        const viewBoxWidth = 600, viewBoxHeight = 300, step = 30;
        const cols = Math.floor(viewBoxWidth / step), rows = Math.floor(viewBoxHeight / step);
        const grid = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ visited: false, walls: [true, true, true, true] })));
        let current = { r: 0, c: 0 };
        grid[current.r][current.c].visited = true;
        const stack = [current];

        while (stack.length > 0) {
            current = stack[stack.length - 1];
            const neighbors = [];
            const { r, c } = current;
            if (r > 0 && !grid[r - 1][c].visited) neighbors.push({ r: r - 1, c, wall: 0, opp: 2 });
            if (c < cols - 1 && !grid[r][c + 1].visited) neighbors.push({ r, c: c + 1, wall: 1, opp: 3 });
            if (r < rows - 1 && !grid[r + 1][c].visited) neighbors.push({ r: r + 1, c, wall: 2, opp: 0 });
            if (c > 0 && !grid[r][c - 1].visited) neighbors.push({ r, c: c - 1, wall: 3, opp: 1 });

            if (neighbors.length > 0) {
                const next = neighbors[Math.floor(Math.random() * neighbors.length)];
                grid[current.r][current.c].walls[next.wall] = false;
                grid[next.r][next.c].walls[next.opp] = false;
                grid[next.r][next.c].visited = true;
                stack.push({ r: next.r, c: next.c });
            } else {
                stack.pop();
            }
        }
        let pathData = '';
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = c * step, y = r * step;
                if (grid[r][c].walls[0]) pathData += `M ${x} ${y} L ${x + step} ${y} `;
                if (grid[r][c].walls[1]) pathData += `M ${x + step} ${y} L ${x + step} ${y + step} `;
            }
        }
        pathElement.setAttribute('d', pathData);
    }
  }

    document.addEventListener("DOMContentLoaded", function () {
      if (!document.querySelector('.customer-sweepstakes-section[data-customer-id]')) return;

      const SUPABASE_URL = window.SUPABASE_CONFIG?.url;
      const SUPABASE_ANON_KEY = window.SUPABASE_CONFIG?.anonKey;

      if (SUPABASE_URL && SUPABASE_ANON_KEY) {
        const customerPanel = new CustomerSweepstakesPanel(SUPABASE_URL, SUPABASE_ANON_KEY);
        customerPanel.init();
      } else {
        console.error("Supabase config missing for customer panel. Please ensure Shopify Customer-Selector tag is included.");
      }
    });

    window.CustomerSweepstakesPanel = CustomerSweepstakesPanel;
}
